import {
  decodeInputFrame,
  encodeInputFrame,
  LOCKSTEP_INPUT_DELAY,
  LOCKSTEP_MAX_FILL,
  LOCKSTEP_MAX_LEAD,
  LOCKSTEP_MIN_LEAD,
  type InputFrame,
  type ServerMessage,
  type WireInputFrame,
} from "@golazo/shared";
import type { OnlineClient } from "./client.js";

const TICK_MS = 1000 / 60;
/**
 * Sin avisos de retraso durante este tiempo, el margen vuelve a bajar un tick.
 *
 * Estaba en 8 s y cada aviso subia DOS ticks o mas: un solo tiron (una pausa
 * del recolector, la simulacion de golpe al entrar a mitad) dejaba el partido
 * con 100-200 ms extra de retraso al pulsar durante minutos. Es el "no es lag,
 * es que tarda en hacer lo que le pido".
 */
const LEAD_DECAY_MS = 3000;
/**
 * Cuanto tiempo de bundles se mira para estimar el reloj del servidor.
 *
 * Se queda con el que MENOS tardo en llegar: ese es el viaje de red limpio, y
 * los demas llevan encima el jitter de la red y del hilo principal. Con el
 * ultimo bundle a secas, cualquier retraso suelto hacia creer que el servidor
 * iba por detras, los inputs llegaban tarde y el margen se inflaba.
 */
const CLOCK_WINDOW_MS = 1000;
/** Ticks de colchon sobre el RTT: cubre el redondeo al tick y el jitter de subida. */
const LEAD_CUSHION = 1;

export interface LockstepOptions {
  client: OnlineClient;
  roomId: string;
  localPlayerId: string;
  startTick?: number;
  seed?: number;
  history?: Array<{ tick: number; frames: Record<string, WireInputFrame> }>;
  /**
   * Partido de una sola persona: se simula en local sin retraso y cada tick
   * jugado se manda al servidor con SU numero, para que quien entre despues
   * pueda rehacerlo. Ver `submitSolo` y `match:handoff`.
   */
  solo?: boolean;
}

export interface LockstepClient {
  readonly roomId: string;
  readonly localPlayerId: string;
  readonly currentTick: number;
  readonly seed: number | null;
  /** Margen de ticks que se esta aplicando al input (diagnostico). */
  readonly lead: number;
  /** Ticks recibidos y aun sin simular: si crece, el cliente va por detras. */
  readonly backlog: number;
  /** true mientras se juega solo, en local. Pasa a false cuando entra alguien. */
  readonly solo: boolean;
  /**
   * Ticks simulados de verdad. Jugando solo `currentTick` no avanza (se juega
   * en local y se apunta aparte), asi que para decirle al servidor "he visto el
   * pitido final en el tick N" hace falta este.
   */
  readonly simulatedTicks: number;
  /** Ha entrado alguien: el partido deja de ser solo tuyo. */
  onHandoff(fn: () => void): () => void;
  onPeerJoined(fn: (playerId: string, nickname: string) => void): () => void;
  onMatchStart(fn: (seed: number, startTick: number) => void): () => void;
  onDisconnect(fn: () => void): () => void;
  onDesync(fn: (tick: number) => void): () => void;
  onMatchOver(fn: (reason: "finished" | "abandoned" | "stopped") => void): () => void;
  onPeerLeft(fn: (playerId: string) => void): () => void;
  /** Envia la huella del estado simulado para que el server detecte divergencias. */
  sendHash(tick: number, hash: string): void;
  ingestHistory(history: Array<{ tick: number; frames: Record<string, WireInputFrame> }>): void;
  replayCatchUp(onTick: (frames: Map<string, InputFrame>) => void): void;
  submitLocal(frame: InputFrame): void;
  /**
   * Jugando solo: apunta `frame` para el siguiente tick y devuelve el frame que
   * hay que SIMULAR, que no es el mismo objeto. Ver la implementacion.
   */
  submitSolo(frame: InputFrame): InputFrame;
  pollFrames(): Map<string, InputFrame> | null;
  advance(): void;
  dispose(): void;
}

export function createLockstep(options: LockstepOptions): LockstepClient {
  const { client, roomId, localPlayerId } = options;
  let currentTick = options.startTick ?? 0;
  let seed: number | null = options.seed ?? null;
  const pending = new Map<number, Map<string, InputFrame>>();
  const matchStartListeners = new Set<(seed: number, startTick: number) => void>();
  const disconnectListeners = new Set<() => void>();
  const desyncListeners = new Set<(tick: number) => void>();
  const matchOverListeners = new Set<(reason: "finished" | "abandoned" | "stopped") => void>();
  const peerLeftListeners = new Set<(playerId: string) => void>();
  const peerJoinedListeners = new Set<(playerId: string, nickname: string) => void>();
  const handoffListeners = new Set<() => void>();
  let solo = options.solo === true;
  /** Jugando solo: el siguiente tick a simular (y a mandar). */
  let soloTick = currentTick;
  let lastFrame: InputFrame | null = null;

  /**
   * Margen adaptativo: ticks EXTRA sobre lo que pide el RTT. Sube cuando el
   * server avisa de que un input llego a un tick ya emitido y baja solo si deja
   * de pasar: en una conexion buena converge al minimo, en una mala se
   * estabiliza donde el juego sigue jugable.
   */
  let penalty = 0;
  let lastLateAt = 0;
  /** Ultimo tick para el que ya se envio input, para no dejar huecos ni repetir. */
  let lastSentTick = -1;
  let lastServerTick = -1;
  /**
   * `llegada - tick * TICK_MS` de los bundles recientes. El servidor emite a
   * 60 Hz exactos contra un origen fijo, asi que este numero seria constante
   * si la red no tuviera jitter: el minimo de la ventana es el mejor viaje.
   */
  const clockSamples: Array<{ at: number; offset: number }> = [];

  const now = (): number =>
    typeof performance !== "undefined" ? performance.now() : Date.now();

  const noteServerTick = (serverTick: number): void => {
    const at = now();
    clockSamples.push({ at, offset: at - serverTick * TICK_MS });
    while (clockSamples.length > 1 && at - clockSamples[0]!.at > CLOCK_WINDOW_MS) clockSamples.shift();
    if (serverTick > lastServerTick) lastServerTick = serverTick;
  };

  /**
   * El tick que el server esta emitiendo AHORA, no el que este cliente simula:
   * apuntar al tick local (que va por detras por el viaje de red) es justo lo
   * que hacia que todo input llegara tarde.
   */
  const estimatedServerTick = (): number => {
    if (clockSamples.length === 0) return currentTick;
    let best = clockSamples[0]!.offset;
    for (const sample of clockSamples) if (sample.offset < best) best = sample.offset;
    return Math.max(lastServerTick, Math.floor((now() - best) / TICK_MS));
  };

  /**
   * Margen minimo que exige la latencia medida.
   *
   * `estimatedServerTick` se apoya en bundles que salieron del server hace
   * RTT/2; el input tarda otro RTT/2 en llegar. O sea que cuando aterriza, el
   * server ya ha avanzado un RTT entero sobre la estimacion: el margen tiene que
   * cubrir el RTT completo, no la mitad. Esperar a los avisos de retraso tambien
   * converge, pero perdiendo input mientras tanto.
   */
  const rttFloor = (): number => {
    const rtt = client.rtt;
    // Sin medida todavia, el valor prudente de siempre.
    if (rtt < 0) return LOCKSTEP_INPUT_DELAY;
    /*
     * El colchon era de +4 ticks (~66 ms) porque la estimacion del reloj se
     * apoyaba en el ultimo bundle y arrastraba todo su jitter. Con el mejor
     * viaje de la ventana basta con uno; si no basta, el servidor avisa y
     * `penalty` sube solo lo que haga falta.
     */
    return Math.ceil(rtt / TICK_MS) + LEAD_CUSHION;
  };

  const effectiveLead = (): number =>
    Math.min(Math.max(rttFloor() + penalty, LOCKSTEP_MIN_LEAD), LOCKSTEP_MAX_LEAD);

  const decayLead = (): void => {
    if (penalty <= 0) return;
    const since = now() - lastLateAt;
    if (since < LEAD_DECAY_MS) return;
    penalty -= 1;
    lastLateAt = now();
  };

  const unsubMsg = client.onMessage((msg: ServerMessage) => {
    if (msg.type === "match:start" && msg.roomId === roomId) {
      seed = msg.seed;
      currentTick = msg.startTick;
      for (const fn of matchStartListeners) fn(msg.seed, msg.startTick);
    }
    if (msg.type === "match:latejoin" && msg.roomId === roomId) {
      seed = msg.seed;
      currentTick = msg.tick;
      const history = msg.history.map((entry) => ({
        tick: entry.tick,
        frames: entry.frames,
      }));
      for (const entry of history) {
        const frames = new Map<string, InputFrame>();
        for (const [playerId, wire] of Object.entries(entry.frames)) {
          frames.set(playerId, decodeInputFrame(wire));
        }
        pending.set(entry.tick, frames);
      }
      if (history.length > 0) {
        currentTick = history[history.length - 1]!.tick + 1;
      }
      noteServerTick(msg.tick);
      for (const fn of matchStartListeners) fn(msg.seed, currentTick);
    }
    if (msg.type === "tick:bundle" && msg.roomId === roomId) {
      const frames = new Map<string, InputFrame>();
      for (const [playerId, wire] of Object.entries(msg.frames)) {
        frames.set(playerId, decodeInputFrame(wire));
      }
      pending.set(msg.tick, frames);
      noteServerTick(msg.tick);
    }
    if (msg.type === "input:late" && msg.roomId === roomId) {
      if (msg.serverTick > lastServerTick) lastServerTick = msg.serverTick;
      /*
       * Un tick por aviso, no `lateBy`. El servidor avisa como mucho dos veces
       * por segundo, asi que una red que empeora de verdad sube igual en un par
       * de segundos; un tiron suelto (muchos ticks tarde de golpe) ya no deja
       * el retraso inflado para el resto del partido.
       */
      penalty = Math.min(penalty + 1, LOCKSTEP_MAX_LEAD);
      lastLateAt = now();
    }
    if (msg.type === "match:handoff" && msg.roomId === roomId && solo) {
      /*
       * Alguien entra. Se para EN EL ACTO, desde el manejador del socket y no
       * desde el bucle de dibujo: asi contesta aunque la pestaña este en
       * segundo plano. Todo lo jugado hasta `soloTick` ya salio por este mismo
       * socket, y el ack va detras, asi que el servidor lo tiene entero.
       */
      solo = false;
      currentTick = soloTick;
      lastSentTick = soloTick - 1;
      client.send({ type: "match:handoff:ack", roomId, tick: soloTick });
      // Sin esto, los primeros ticks de lockstep llegan sin input tuyo y el
      // jugador se planta en seco justo al entrar el otro.
      if (lastFrame) api.submitLocal(lastFrame);
      for (const fn of handoffListeners) fn();
    }
    if (msg.type === "peer:joined" && msg.roomId === roomId) {
      for (const fn of peerJoinedListeners) fn(msg.playerId, msg.nickname);
    }
    if (msg.type === "peer:left" && msg.roomId === roomId) {
      // El asiento pasa a bot: el server deja de incluir su input y la IA lo lleva.
      for (const fn of peerLeftListeners) fn(msg.playerId);
    }
    if (msg.type === "sync:desync" && msg.roomId === roomId) {
      for (const fn of desyncListeners) fn(msg.tick);
    }
    if (msg.type === "match:over" && msg.roomId === roomId) {
      for (const fn of matchOverListeners) fn(msg.reason);
    }
  });

  const unsubState = client.onState((state) => {
    if (state === "offline") {
      for (const fn of disconnectListeners) fn();
    }
  });

  if (options.history?.length) {
    for (const entry of options.history) {
      const frames = new Map<string, InputFrame>();
      for (const [playerId, wire] of Object.entries(entry.frames)) {
        frames.set(playerId, decodeInputFrame(wire));
      }
      pending.set(entry.tick, frames);
    }
    const last = options.history[options.history.length - 1]!.tick;
    currentTick = last + 1;
  }

  /*
   * Lo que el servidor ya emitio antes de que este lockstep existiera. Sin
   * esto, un bundle que llega entre el `match:latejoin` y la creacion del
   * lockstep deja un hueco en `pending` que nunca se rellena: el partido se
   * congela para quien entra. Ver `RECENT_BUNDLES` en client.ts.
   */
  for (const bundle of client.recentBundles?.(roomId) ?? []) {
    if (bundle.tick < currentTick || pending.has(bundle.tick)) continue;
    const frames = new Map<string, InputFrame>();
    for (const [playerId, wire] of Object.entries(bundle.frames)) {
      frames.set(playerId, decodeInputFrame(wire));
    }
    pending.set(bundle.tick, frames);
    // Sin `noteServerTick`: la hora de llegada de estos ya no es la de ahora y
    // falsearia la estimacion del reloj. El siguiente bundle vivo la pone.
    if (bundle.tick > lastServerTick) lastServerTick = bundle.tick;
  }

  if (seed !== null) {
    for (const fn of matchStartListeners) fn(seed, currentTick);
  }

  const api: LockstepClient = {
    roomId,
    localPlayerId,
    get currentTick() {
      return currentTick;
    },
    get seed() {
      return seed;
    },
    onMatchStart(fn) {
      matchStartListeners.add(fn);
      if (seed !== null) fn(seed, currentTick);
      return () => matchStartListeners.delete(fn);
    },
    onDisconnect(fn) {
      disconnectListeners.add(fn);
      return () => disconnectListeners.delete(fn);
    },
    ingestHistory(history) {
      for (const entry of history) {
        const frames = new Map<string, InputFrame>();
        for (const [playerId, wire] of Object.entries(entry.frames)) {
          frames.set(playerId, decodeInputFrame(wire));
        }
        pending.set(entry.tick, frames);
      }
      if (history.length > 0) {
        const last = history[history.length - 1]!.tick;
        currentTick = last + 1;
      }
    },
    replayCatchUp(onTick) {
      const ticks = [...pending.keys()].filter((t) => t < currentTick).sort((a, b) => a - b);
      for (const t of ticks) {
        const frames = pending.get(t);
        if (!frames) continue;
        onTick(frames);
        pending.delete(t);
      }
    },
    get lead() {
      return effectiveLead();
    },
    get backlog() {
      return pending.size;
    },
    get solo() {
      return solo;
    },
    get simulatedTicks() {
      return solo ? soloTick : currentTick;
    },
    onHandoff(fn) {
      handoffListeners.add(fn);
      return () => handoffListeners.delete(fn);
    },
    onPeerJoined(fn) {
      peerJoinedListeners.add(fn);
      return () => peerJoinedListeners.delete(fn);
    },
    onDesync(fn) {
      desyncListeners.add(fn);
      return () => desyncListeners.delete(fn);
    },
    onMatchOver(fn) {
      matchOverListeners.add(fn);
      return () => matchOverListeners.delete(fn);
    },
    onPeerLeft(fn) {
      peerLeftListeners.add(fn);
      return () => peerLeftListeners.delete(fn);
    },
    sendHash(tick: number, hash: string) {
      client.send({ type: "sync:hash", roomId, tick, hash });
    },
    submitSolo(frame: InputFrame) {
      /*
       * Se simula la copia que viaja, no el objeto del teclado. El teclado
       * devuelve SIEMPRE el mismo frame y le cambia `move` y los conjuntos por
       * dentro en cada tick; quien entra rehace el partido con copias limpias
       * salidas del JSON. Hoy la simulacion no se queda con referencias al
       * frame (comprobado), pero el dia que lo haga el anfitrion veria cambiar
       * su pasado y el otro no. Asi los dos simulan exactamente el mismo valor.
       */
      const wire = encodeInputFrame(frame);
      const canonical = decodeInputFrame(wire);
      if (!solo) return canonical;
      lastFrame = canonical;
      client.send({ type: "input:submit", roomId, tick: soloTick, playerId: localPlayerId, frame: wire });
      soloTick += 1;
      return canonical;
    },
    submitLocal(frame: InputFrame) {
      if (solo) return;
      // Copia: el del teclado se reescribe en sitio y aqui se guarda para despues.
      lastFrame = decodeInputFrame(encodeInputFrame(frame));
      decayLead();
      const target = estimatedServerTick() + effectiveLead();
      // Ya cubierto (el reloj estimado no avanza en cada frame de render, y a
      // veces retrocede por jitter): reenviar un tick viejo solo genera avisos
      // de retraso.
      if (target <= lastSentTick) return;

      const wire = encodeInputFrame(frame);
      // Cuando el margen sube, el objetivo SALTA y los ticks intermedios se
      // quedarian sin input: el server los rellena con neutro y el jugador se
      // planta en seco justo cuando la conexion empeora. Mantener el ultimo
      // input en el hueco es mucho mas fiel a lo que el jugador esta haciendo.
      const first = Math.max(lastSentTick + 1, target - LOCKSTEP_MAX_FILL);
      for (let t = first; t <= target; t++) {
        client.send({
          type: "input:submit",
          roomId,
          tick: t,
          playerId: localPlayerId,
          frame: wire,
        });
      }
      lastSentTick = target;
    },
    pollFrames() {
      return pending.get(currentTick) ?? null;
    },
    advance() {
      pending.delete(currentTick);
      currentTick += 1;
    },
    dispose() {
      unsubMsg();
      unsubState();
      matchStartListeners.clear();
      disconnectListeners.clear();
      desyncListeners.clear();
      matchOverListeners.clear();
      peerLeftListeners.clear();
      peerJoinedListeners.clear();
      handoffListeners.clear();
      pending.clear();
    },
  };
  return api;
}

export type { LockstepClient as Lockstep };
